import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { DEFAULT_VISIBLE_PAGES_RANGE } from '../../constants/constants';
import { AumPaginatorIntl } from './pagination-intl';

const debug = Debug('aumico:aumico-lib-core-fe:AumPaginationActionBar');
@UntilDestroy()
@Component({
    selector: 'aum-pagination-action-bar',
    styleUrls: ['./pagination-action-bar.scss'],
    templateUrl: './pagination-action-bar.html',
})
export class AumPaginationActionBar extends MatPaginator implements OnInit, AfterViewChecked {
    pageSizePropertyOptions: INameId[];
    pageSizeFormControl: UntypedFormControl = new UntypedFormControl('', []);
    pages: number[] = [];

    @Input() userPreferredNumberOfDocumentsPerPage: number | undefined;

    constructor(
        public override _intl: AumPaginatorIntl,
        _changeDetectorRef: ChangeDetectorRef,
        public elRef: ElementRef,
        private i18nextService: I18NextService,
    ) {
        super(_intl, _changeDetectorRef);
        this.applyLocalizations();
    }
    override ngOnInit(): void {
        super.ngOnInit();
        this.mapPageSizeOptionsToPropertyOptions();

        const isSelectedPageSizeValid = this.pageSizeOptions.find(
            (size) => size === this.userPreferredNumberOfDocumentsPerPage,
        );
        if (isSelectedPageSizeValid != null) {
            this.pageSize = this.userPreferredNumberOfDocumentsPerPage;
        }

        this.setPageSizeDefaultOption();
        this.page.pipe(untilDestroyed(this)).subscribe(() => {
            this.pages = this.getPagesRange();
        });
    }
    ngAfterViewChecked(): void {
        this.pages = this.getPagesRange();
    }

    private applyLocalizations(): void {
        const rangeTextLocalization = this.i18nextService.t('aumico_core_pagination_action_bar_range_text') as string;
        if (rangeTextLocalization != null) {
            this._intl.setRangeText(rangeTextLocalization);
        }
        if (this.pageIndex != null && this.pageSize != null && this.length != null) {
            this._intl.getRangeLabel(this.pageIndex, this.pageSize, this.length);
        }
    }

    private mapPageSizeOptionsToPropertyOptions(): void {
        this.pageSizePropertyOptions = this._displayedPageSizeOptions.map((option) => ({
            id: option.toString(),
            name: option.toString(),
        }));
    }

    private setPageSizeDefaultOption(): void {
        this.pageSizeFormControl.setValue(
            this.pageSizePropertyOptions.find((option) => option.id == this.pageSize.toString()),
        );
    }

    jumpToPage(index: number): void {
        debug('jumpToPage', index, this.page);
        this.pageIndex = index;
        const pageEvent: PageEvent = {
            pageIndex: index,
            pageSize: this.pageSize,
            length: this.length,
        };
        this.page.next(pageEvent);
    }

    private getPagesRange(): Array<number> {
        const isNumberOfVisiblePagesEven = DEFAULT_VISIBLE_PAGES_RANGE % 2 === 0;
        const halfOfVisiblePages = Math.floor(DEFAULT_VISIBLE_PAGES_RANGE / 2);
        const firstVisiblePageIndex = this.pageIndex - halfOfVisiblePages;
        const lastVisiblePageIndex = this.pageIndex + halfOfVisiblePages;
        const totalNumberOfPages = this.getNumberOfPages();
        const lastVisiblePagesStartIndex = totalNumberOfPages - DEFAULT_VISIBLE_PAGES_RANGE;
        const isInLastVisiblePages = this.pageIndex >= totalNumberOfPages - halfOfVisiblePages;

        if (isNumberOfVisiblePagesEven) {
            throw new Error('Number of visible pages must be odd');
        }

        if (totalNumberOfPages <= DEFAULT_VISIBLE_PAGES_RANGE) {
            return this.rangeOfPages(0, totalNumberOfPages - 1);
        }

        if (this.pageIndex < halfOfVisiblePages) {
            return this.rangeOfPages(0, DEFAULT_VISIBLE_PAGES_RANGE - 1);
        }

        if (isInLastVisiblePages) {
            return this.rangeOfPages(lastVisiblePagesStartIndex, totalNumberOfPages - 1);
        }

        return this.rangeOfPages(firstVisiblePageIndex, lastVisiblePageIndex);
    }

    private rangeOfPages(start: number, end: number): Array<number> {
        const pages: Array<number> = [];
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    }

    public onPaginationSelectionChange(numberPerPage: number): void {
        this.pageSize = numberPerPage;
        this._changePageSize(numberPerPage);
        this.page.next({
            pageSize: this.pageSize,
            pageIndex: this.pageIndex,
            length: this.length,
        });
    }
}
